/**
 * BONE WATER — SCROLL REVEAL
 * assets/bw-reveal.js
 *
 * Brings content in as it scrolls into view, and brings each section in the
 * way that section is built: headline lines wipe up out of their baseline,
 * eyebrows track in from wide letter-spacing, the two blend panels slide in
 * from opposite sides, step numbers stamp down, stats count up from zero,
 * press logos sharpen out of a blur, table cells fill in row by row, cards
 * unfold from their top edge. Nothing is one fade-and-rise for everything.
 *
 * HOW A SECTION IS RECOGNISED
 * Every Bone Water section has a root element with a short class (.bw-hero,
 * .bw-faq, .bw-act …). RECIPES maps that root to a list of rules, each a
 * selector and an effect. Rules run in order inside the section; anything
 * the rules did not name falls to the generic pass, which tags the pieces
 * worth animating (cards, rows, list items as units; headings, paragraphs,
 * buttons, images as leaves) with the plain rise. Horizon sections have no
 * recipe and get the generic pass alone.
 *
 * THE EFFECTS (styles in bw-tokens.css, [data-bw-reveal="…"])
 *   up / left / right / drop   rise, or slide in from a side or from above
 *   pop                        scale up from small on a springy curve
 *   scale / zoom               settle from slightly small / slightly large
 *   blur                       sharpen in, for small marks and logos only
 *   wipe-up                    clipped at the baseline, then wiped upward
 *   wipe                       clipped at the left edge, then wiped across
 *   unfold                     squashed toward its top edge, then opens out
 *   stamp                      drops in from oversized, like a stamp
 *   spin                       rotates in
 *   track                      letter-spacing closes from wide to set
 *   count                      rises, and its number counts up from zero
 *
 * WHAT IT WILL NOT TOUCH
 * The header, the marquee (already in motion), the hero floats (own
 * entrance), anything inside a dialog or drawer, and anything that has no
 * box at load — the inactive blend's hidden copy, a countdown not yet shown —
 * so a blend switch still flips instantly. Reduced motion turns everything
 * off: nothing is tagged, nothing is hidden, numbers are already final.
 *
 * HOW IT STAYS SAFE
 *   - The hidden state lives on an attribute only this script adds. No
 *     script, nothing hidden.
 *   - Once a piece has played, the attribute comes off again, so its own
 *     hover transitions, opacity states and transforms are exactly as the
 *     section wrote them. The reveal borrows the element for about a second.
 *   - Motion uses the individual translate / scale / rotate properties, not
 *     transform, so it composes with any transform the element already has.
 *
 * Requires: assets/bw-tokens.css
 */
(function () {
  if (window.__bwRevealBound) return;
  window.__bwRevealBound = true;

  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Keep in step with the durations in bw-tokens.css. */
  var DUR = 700;
  var LONG = { zoom: 1300, blur: 900, count: 1400 };
  var STAGGER = 70;
  var MAX_STAGGER = 8;
  var COUNT_MS = 1400;

  var SKIP = [
    'header', '.bw-header', '.shopify-section-group-header-group',
    '.bw-marquee', '.bw-float',
    'dialog', '[role="dialog"]', '[popover]',
    'theme-drawer', 'dialog-component', 'cart-drawer-component',
    'header-drawer', 'quick-add-dialog', 'zoom-dialog',
    /* The reserve band renders its own pricing grid and must be readable on
       first paint — see the gate exemption in bw-tokens.css. A section whose
       whole job is a price cannot afford to be blank while a script decides
       how to animate it. */
    '.bw-build-box-section',
    '.bw-liquid', '.bw-liquid__ghost',
    '[data-bw-no-reveal]'
  ].join(', ');

  var UNIT = [
    '[class*="__card"]', '[class*="__row"]', '[class*="__item"]',
    '[class*="__step"]', '[class*="__stat"]', '[class*="__actions"]',
    '[class*="__specs"]', '[class*="__pills"]',
    'details', 'figure', 'picture', 'table', 'form', 'li',
    '.card', '.product-card', '.group-block', '.text-block'
  ].join(', ');

  var LEAF = [
    'h1', 'h2', 'h3', 'h4', 'p', 'img', 'video', 'blockquote',
    '.bw-btn', '.button', '.bw-pill', '.bw-timestamp',
    '[class*="__eyebrow"]', '[class*="__lede"]', '[class*="__price"]',
    '[class*="__rating"]', '[class*="__count"]', '[class*="__note"]'
  ].join(', ');

  var ALL = UNIT + ', ' + LEAF;

  /* An effect may be a string or a function of (element, index-in-rule),
     which is how the two blend panels get opposite directions. */
  function alternate(a, b) { return function (el, i) { return i % 2 ? b : a; }; }

  var RECIPES = [
    { root: '.bw-hero', stagger: 80, rules: [
      ['.bw-hero__eyebrow', 'track'],
      ['.bw-hero__line', 'wipe-up'],
      ['.bw-hero__lede', 'up'],
      ['.bw-hero__actions > *', 'pop'],
      ['.bw-hero__pills > *', 'left'],
      ['.bw-hero__rating', 'up']
    ] },

    { root: '.bw-bh', stagger: 80, rules: [
      ['.bw-bh__eyebrow', 'track'],
      ['.bw-bh__name', 'wipe-up'],
      ['.bw-bh__rating', 'up'],
      ['.bw-bh__lede', 'up'],
      ['.bw-bh__pills > *', 'left'],
      ['.bw-bh__price', 'pop'],
      ['.bw-bh__actions > *', 'pop'],
      ['.bw-bh__ghost', 'zoom']
    ] },

    { root: '.bw-splithero', stagger: 90, rules: [
      ['.bw-splithero__eyebrow', 'track'],
      ['.bw-splithero__h', 'wipe-up'],
      ['.bw-splithero__lede', 'up'],
      ['.bw-splithero__actions > *', 'pop'],
      ['.bw-panel', alternate('left', 'right')]
    ] },

    { root: '.bw-proof', stagger: 110, rules: [
      ['.bw-stat__value', 'count'],
      ['.bw-stat__stars', 'pop'],
      ['.bw-stat__label', 'up']
    ] },

    { root: '.bw-numbers', stagger: 120, rules: [
      ['.bw-numbers__eyebrow', 'track'],
      ['.bw-numbers__head', 'wipe-up'],
      ['.bw-num__value', 'count'],
      ['.bw-num__title', 'up'],
      ['.bw-num__body', 'up']
    ] },

    { root: '.bw-htu', stagger: 110, rules: [
      ['.bw-htu__eyebrow', 'track'],
      ['.bw-htu__head', 'wipe-up'],
      ['.bw-htu__num', 'stamp'],
      ['.bw-htu__title', 'left'],
      ['.bw-htu__body', 'up']
    ] },

    { root: '.bw-act', stagger: 60, rules: [
      ['.bw-act__eyebrow', 'track'],
      ['.bw-act__head', 'wipe-up'],
      ['.bw-act__countnum', 'count'],
      ['.bw-act__pills > *', 'pop'],
      ['.bw-act__row', 'left']
    ] },

    { root: '.bw-bs', stagger: 90, rules: [
      ['.bw-bs__head', 'wipe-up'],
      ['.bw-bs__chip', 'pop'],
      ['.bw-bs__note', 'up'],
      ['.bw-card', 'unfold']
    ] },

    { root: '.bw-press', stagger: 70, rules: [
      ['.bw-press__label', 'track'],
      ['.bw-press__item', 'blur']
    ] },

    { root: '.bw-compare', stagger: 40, rules: [
      ['.bw-compare__eyebrow', 'track'],
      ['.bw-compare__head', 'wipe-up'],
      ['.bw-compare__table th, .bw-compare__table td', 'left'],
      ['.bw-compare__note', 'up']
    ] },

    { root: '.bw-faq', stagger: 80, rules: [
      ['.bw-faq__eyebrow', 'track'],
      ['.bw-faq__head', 'wipe-up'],
      ['.bw-faq__mark', 'spin'],
      ['.bw-faq__item', 'left']
    ] },

    { root: '.bw-offer', stagger: 90, rules: [
      ['.bw-offer__eyebrow', 'track'],
      ['.bw-offer__h2', 'wipe-up'],
      ['.bw-offer__lede', 'up'],
      ['.bw-offer__card', 'unfold'],
      ['.bw-offer__price', 'pop'],
      ['.bw-offer__minor', 'right'],
      ['.bw-offer__next', 'up'],
      ['.bw-offer__foot', 'up']
    ] },

    { root: '.bw-footer', stagger: 90, rules: [
      ['.bw-footer__eyebrow', 'track'],
      ['.bw-footer__head', 'wipe-up'],
      ['.bw-footer__signup-form', 'right'],
      ['.bw-footer__col', 'up'],
      ['.bw-footer__mark-text', 'wipe-up']
    ] },

    { root: '.bw-fp', stagger: 100, rules: [
      ['.bw-fp__img', 'zoom'],
      ['.bw-fp__eyebrow', 'track'],
      ['.bw-fp__title', 'wipe-up'],
      ['.bw-fp__note', 'right']
    ] },

    { root: '.bw-cap', stagger: 80, rules: [
      ['.bw-cap__eyebrow', 'track'],
      ['.bw-cap__h', 'wipe-up'],
      ['.bw-cap__body', 'up'],
      ['.bw-cap__row', 'pop']
    ] },

    { root: '.bw-ct', stagger: 70, rules: [
      ['.bw-ct__eyebrow', 'track'],
      ['.bw-ct__h1', 'wipe-up'],
      ['.bw-ct__lede', 'up'],
      ['.bw-ct__field', 'up'],
      ['.bw-ct__actions', 'pop']
    ] },

    { root: '.bw-res', stagger: 90, rules: [
      ['.bw-res__eyebrow', 'track'],
      ['.bw-res__head', 'wipe-up'],
      ['.bw-res__lede', 'up'],
      ['.bw-res__panel', 'unfold']
    ] },
  ];

  var io = new IntersectionObserver(onIntersect, {
    rootMargin: '0px 0px -10% 0px',
    threshold: 0.08
  });

  /* ---------------------------------------------------------------------- */

  /**
   * Everything that came into view on the same tick is one batch. Sorting it
   * by where it sits on screen and spacing the delays out is what turns a
   * section arriving at once into a section unfolding downward. Pieces that
   * enter one at a time while scrolling get no delay at all.
   */
  function onIntersect(entries) {
    var batch = [];
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      batch.push(entries[i]);
      io.unobserve(entries[i].target);
    }
    if (!batch.length) return;

    batch.sort(function (a, b) {
      var ra = a.boundingClientRect, rb = b.boundingClientRect;
      return (ra.top - rb.top) || (ra.left - rb.left);
    });

    for (var j = 0; j < batch.length; j++) {
      var el = batch[j].target;
      reveal(el, Math.min(j, MAX_STAGGER) * (el.__bwStagger || STAGGER));
    }
  }

  function reveal(el, delay) {
    var fx = el.getAttribute('data-bw-reveal') || 'up';
    var dur = LONG[fx] || DUR;

    /* The wipes hide with a clip-path, and a fully clipped element has no
       visible area — which Chrome's IntersectionObserver takes literally, so
       it would never report the element as intersecting and the headline
       would never come back. Until now the element has only been transparent.
       Here, at the moment it is due, the clip is put on with transitions off,
       a reflow commits it, and only then does .is-in start the wipe from it. */
    if (fx === 'wipe-up' || fx === 'wipe') {
      el.classList.add('is-set');
      void el.offsetWidth;
    }

    el.style.transitionDelay = delay + 'ms';
    el.classList.add('is-in');
    if (fx === 'count') setTimeout(function () { countUp(el); }, delay);

    setTimeout(function () {
      el.__bwRevealed = true;
      el.removeAttribute('data-bw-reveal');
      el.classList.remove('is-in', 'is-set');
      el.style.transitionDelay = '';
    }, delay + dur + 80);
  }

  /* ---------------------------------------------------------------------- */

  /**
   * Counts every number in the element up from zero, in place, keeping
   * whatever sits around it — "12,000+" runs 0 → 12,000 and keeps the plus,
   * "4.9" keeps one decimal, "$29" keeps the dollar. Works on text nodes, so
   * the dual-blend markup (two spans, one hidden) counts both and the hidden
   * one is simply final by the time it is shown.
   */
  function countUp(el) {
    var nodes = [];
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    var n;
    while ((n = walker.nextNode())) {
      var m = /^(\s*[^\d\s]*\s*)(\d[\d,]*)(\.\d+)?(.*)$/.exec(n.nodeValue);
      if (!m) continue;
      var whole = m[2].replace(/,/g, '');
      nodes.push({
        node: n,
        pre: m[1],
        post: m[4],
        grouped: m[2].indexOf(',') !== -1,
        decimals: m[3] ? m[3].length - 1 : 0,
        value: parseFloat(whole + (m[3] || ''))
      });
    }
    if (!nodes.length) return;

    var t0 = performance.now();
    function tick(now) {
      var p = Math.min((now - t0) / COUNT_MS, 1);
      var e = 1 - Math.pow(1 - p, 4); /* ease-out quart: fast start, soft landing */
      for (var i = 0; i < nodes.length; i++) {
        var c = nodes[i];
        c.node.nodeValue = c.pre + format(c.value * e, c.decimals, c.grouped) + c.post;
      }
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function format(v, decimals, grouped) {
    var s = v.toFixed(decimals);
    if (!grouped) return s;
    var parts = s.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  /* ---------------------------------------------------------------------- */

  function eligible(el) {
    if (el.__bwRevealed || el.hasAttribute('data-bw-reveal')) return false;
    if (el.closest(SKIP)) return false;
    /* No box means display:none — the inactive blend, a countdown not yet
       shown. Not part of the page yet, so it is left to appear the way its
       section intended. This is the only layout read in the walk. */
    if (!el.getClientRects().length) return false;
    return true;
  }

  function tag(el, fx, stagger) {
    el.setAttribute('data-bw-reveal', fx);
    el.__bwStagger = stagger;
    io.observe(el);
  }

  /**
   * The recipe pass. Rules are explicit, so a rule may name something inside
   * an earlier rule's element (the FAQ marker inside the FAQ row): the row
   * slides in and the marker spins in on top of it. Reads first, then writes.
   */
  function applyRecipe(section, recipe) {
    var picks = [];
    for (var r = 0; r < recipe.rules.length; r++) {
      var sel = recipe.rules[r][0];
      var fx = recipe.rules[r][1];
      var found = section.querySelectorAll(sel);
      var k = 0;
      for (var i = 0; i < found.length; i++) {
        var el = found[i];
        if (!eligible(el) || el.__bwPicked) continue;
        el.__bwPicked = true;
        picks.push([el, typeof fx === 'function' ? fx(el, k) : fx]);
        k++;
      }
    }
    for (var j = 0; j < picks.length; j++) {
      tag(picks[j][0], picks[j][1], recipe.stagger);
      picks[j][0].__bwPicked = false;
    }
  }

  /**
   * The generic pass, for whatever the recipe did not name and for sections
   * with no recipe. Outermost match wins, and anything that already contains
   * a tagged piece is left alone so a wrapper never rises on top of the
   * choreography inside it.
   */
  function applyGeneric(section, stagger) {
    var found = section.querySelectorAll(ALL);
    var chosen = new Set();
    var i, el, a, nested;

    for (i = 0; i < found.length; i++) {
      el = found[i];
      if (!eligible(el)) continue;
      if (el.querySelector('[data-bw-reveal]')) continue;

      nested = false;
      a = el.parentElement;
      while (a && a !== section) {
        if (a.__bwRevealed || chosen.has(a) || a.hasAttribute('data-bw-reveal')) { nested = true; break; }
        a = a.parentElement;
      }
      if (nested) continue;
      chosen.add(el);
    }

    chosen.forEach(function (node) { tag(node, 'up', stagger); });
  }

  function collect(section) {
    if (section.matches(SKIP) || section.closest(SKIP)) return;

    var recipe = null;
    for (var i = 0; i < RECIPES.length; i++) {
      if (section.querySelector(RECIPES[i].root)) { recipe = RECIPES[i]; break; }
    }
    if (recipe) applyRecipe(section, recipe);
    applyGeneric(section, recipe ? recipe.stagger : STAGGER);
  }

  function mount(root) {
    root = root || document;
    if (root !== document && root.classList && root.classList.contains('shopify-section')) {
      collect(root);
      return;
    }
    var sections = root.querySelectorAll('.shopify-section');
    for (var i = 0; i < sections.length; i++) collect(sections[i]);

    /* The inline script in snippets/bw-head.liquid hid every section before
       first paint so nothing could flash at rest and then animate. Tagging
       is done, so let the page show: untagged pieces appear as they are,
       tagged ones are already in their hidden state and will reveal. */
    document.documentElement.classList.remove('bw-reveal-pending');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }

  /* The theme editor re-renders a section into fresh DOM. Tag the new nodes;
     the observer reveals them on the next paint, exactly as on a live page. */
  document.addEventListener('shopify:section:load', function (e) {
    mount(e.target);
  });
}());
